<template>
  <div v-if="hasData" class="action-plan-logs-container">
    <div
      v-for="(history, historyIndex) in histories"
      :key="`history-${historyIndex}`"
      class="action-plan-history-card"
    >
      <!-- Header de cada history -->
      <div class="action-plan-header" @click="toggleHistoryCollapse(historyIndex)">
        <div class="d-flex align-items-center justify-content-between w-100">
          <div class="d-flex align-items-center gap-1">
            <feather-icon
              :icon="getTerminalIcon(history.terminal_type)"
              size="18"
              class="mr-1"
            />
            <span class="action-plan-name">{{ getActionPlanName(history) }}</span>
            <b-badge
              :variant="getTerminalBadgeVariant(history.terminal_type)"
              class="ml-2 text-wrap"
              style="max-width: 120px"
            >
              {{ getTerminalTypeLabel(history.terminal_type) }}
            </b-badge>
          </div>
          <div class="d-flex align-items-center gap-1">
            <small class="text-muted">
              {{ $t("alarm.timeline.duration") }}:
              {{ formatDuration(history.started_at, history.ended_at) }}
            </small>
            <feather-icon
              icon="ChevronDownIcon"
              size="16"
              class="chevron-icon"
              :class="{ rotated: openHistories[historyIndex] }"
            />
          </div>
        </div>
      </div>

      <!-- Contenido colapsable de cada history -->
      <b-collapse
        :id="`history-collapse-${historyIndex}`"
        v-model="openHistories[historyIndex]"
        class="action-plan-collapse"
      >
        <div class="action-plan-body">
          <!-- Información general del plan -->
          <div class="plan-info-grid mb-2">
            <div class="info-item">
              <span class="info-label">{{ $t("alarm.timeline.started_at") }}:</span>
              <span class="info-value">{{ formatDate(history.started_at) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ $t("alarm.timeline.ended_at") }}:</span>
              <span class="info-value">{{ formatDate(history.ended_at) }}</span>
            </div>
            <div
              v-if="history.terminal_meta && history.terminal_meta.reason"
              class="info-item"
            >
              <span class="info-label">{{ $t("alarm.timeline.reason") }}:</span>
              <span class="info-value">{{ history.terminal_meta.reason }}</span>
            </div>
            <!-- Información del usuario -->
            <div v-if="history.user" class="info-item user-info">
              <span class="info-label">{{ $t("alarm.timeline.user_info") }}:</span>
              <div class="info-value">
                <div v-if="history.user.name" class="user-name">
                  {{ history.user.name }}
                </div>
                <div v-if="history.user.email" class="user-email text-muted">
                  <small
                    >{{ $t("alarm.timeline.email") }}: {{ history.user.email }}</small
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- Timeline de pasos -->
          <div class="steps-timeline">
            <h6 class="mb-2">{{ $t("alarm.timeline.steps") }}</h6>
            <div
              v-for="(step, stepIndex) in history.path.slice(1)"
              :key="`step-${stepIndex}`"
              class="timeline-step"
              :class="step.type.toLowerCase()"
            >
              <div class="step-icon-container" :class="getStepIconColor(step.type)">
                <feather-icon
                  class="step-icon"
                  :icon="getStepIcon(step.type, step)"
                  size="14"
                />
              </div>

              <div class="step-content">
                <!-- ActionPlan Step -->
                <div v-if="step.type === 'ActionPlan'" class="step-actionplan">
                  <div class="step-header">
                    <strong>{{ step.name || $t("alarm.timeline.action_plan") }}</strong>
                    <small v-if="step.data.timestamp" class="text-muted ml-2">
                      {{ formatDate(step.data.timestamp) }}
                    </small>
                  </div>
                  <!-- Comentarios si existen -->
                  <div v-if="step.data.comments" class="step-comments mt-2">
                    <strong>{{ $t("alarm.timeline.comments") }}:</strong>
                    <div class="comments-text">{{ step.data.comments }}</div>
                  </div>
                </div>

                <!-- Decision Step -->
                <div v-else-if="step.type === 'Decision'" class="step-decision">
                  <div class="step-header d-flex align-items-center">
                    <strong>{{ $t("alarm.timeline.decision") }}</strong>
                    <small v-if="step.data.timestamp" class="text-muted ml-2">
                      {{ formatDate(step.data.timestamp) }}
                    </small>
                  </div>
                  <div class="decision-content mt-2">
                    <div class="question mb-2">
                      <strong>{{ $t("alarm.timeline.question") }}:</strong>
                      {{ step.node_data.question }}
                    </div>

                    <div v-if="step.node_data.answer_options" class="answer-options">
                      <strong>{{ $t("alarm.timeline.answer_options") }}:</strong>
                      <div class="options-chips mt-1">
                        <b-badge
                          v-for="option in step.node_data.answer_options"
                          :key="option"
                          variant="light"
                          class="mr-1 mb-1"
                        >
                          {{ option }}
                        </b-badge>
                      </div>
                    </div>
                    <div v-if="step.data.answer" class="answer mb-2">
                      <strong>{{ $t("alarm.timeline.answer") }}:</strong>
                      <b-badge variant="success" class="ml-1">{{
                        step.data.answer
                      }}</b-badge>
                    </div>
                  </div>
                  <!-- Archivos si existen -->
                  <div
                    v-if="step.data.files && step.data.files.length > 0"
                    class="step-files mt-2"
                  >
                    <strong>{{ $t("alarm.timeline.files") }}:</strong>
                    <div class="files-list mt-1">
                      <b-badge
                        v-for="file in step.data.files"
                        :key="file"
                        variant="info"
                        class="mr-1 mb-1"
                      >
                        {{ file }}
                      </b-badge>
                    </div>
                  </div>
                  <!-- Comentarios si existen -->
                  <div v-if="step.data.comments" class="step-comments mt-2">
                    <strong>{{ $t("alarm.timeline.comments") }}:</strong>
                    <div class="comments-text">{{ step.data.comments }}</div>
                  </div>
                </div>

                <!-- Node Step -->
                <div v-else-if="step.type === 'Node'" class="step-node">
                  <div class="step-header d-flex align-items-center">
                    <strong>{{
                      step.node_data.filled_stack &&
                      step.node_data.filled_stack.length > 0
                        ? $t("alarm.timeline.command")
                        : $t("alarm.timeline.node")
                    }}</strong>
                    <small v-if="step.data.timestamp" class="text-muted ml-2">
                      {{ formatDate(step.data.timestamp) }}
                    </small>
                  </div>
                  <div class="node-content mt-2">
                    <div class="node-grid">
                      <div class="info-item">
                        <span class="info-label">{{ $t("alarm.status") }}:</span>
                        <span class="info-value">
                          <b-badge :variant="getStatusBadgeVariant(step.data.status)">
                            {{ step.data.status }}
                          </b-badge>
                        </span>
                      </div>
                    </div>

                    <!-- Archivos si existen -->
                    <div
                      v-if="step.data.files && step.data.files.length > 0"
                      class="step-files mt-2"
                    >
                      <strong>{{ $t("alarm.timeline.files") }}:</strong>
                      <div class="files-list mt-1">
                        <b-badge
                          v-for="file in step.data.files"
                          :key="file"
                          variant="info"
                          class="mr-1 mb-1"
                        >
                          {{ file }}
                        </b-badge>
                      </div>
                    </div>

                    <!-- Comentarios si existen -->
                    <div v-if="step.data.comments" class="step-comments mt-2">
                      <strong>{{ $t("alarm.timeline.comments") }}:</strong>
                      <div class="comments-text">{{ step.data.comments }}</div>
                    </div>

                    <!-- Node Data -->
                    <div
                      v-if="
                        step.node_data.data && Object.keys(step.node_data.data).length > 0
                      "
                      class="node-data mt-2"
                    >
                      <strong>{{ $t("alarm.timeline.command_data") }}:</strong>

                      <!-- Special handling for camera_round command -->
                      <div
                        v-if="step.node_data.cmd === 'camera_round'"
                        class="camera-round-data mt-1"
                      >
                        <div class="data-grid">
                          <div v-if="step.node_data.data.cameras" class="data-item">
                            <span class="data-key"
                              >{{ $t("alarm.timeline.cameras") }}:</span
                            >
                            <div class="data-value">
                              <div class="chips mt-1">
                                <b-badge
                                  v-for="camera in step.node_data.data.cameras"
                                  :key="camera.value || camera"
                                  variant="info"
                                  class="mr-1 mb-1"
                                >
                                  {{ camera.label || camera }}
                                </b-badge>
                              </div>
                            </div>
                          </div>
                          <div v-if="step.node_data.data.users" class="data-item">
                            <span class="data-key"
                              >{{ $t("alarm.timeline.users") }}:</span
                            >
                            <div class="data-value">
                              <div class="chips mt-1">
                                <b-badge
                                  v-for="user in step.node_data.data.users"
                                  :key="user"
                                  variant="secondary"
                                  class="mr-1 mb-1"
                                >
                                  {{ user }}
                                </b-badge>
                              </div>
                            </div>
                          </div>
                          <div v-if="step.node_data.data.location" class="data-item">
                            <span class="data-key"
                              >{{ $t("alarm.timeline.location") }}:</span
                            >
                            <span class="data-value">{{
                              step.node_data.data.location
                            }}</span>
                          </div>
                          <div v-if="step.node_data.data.loop" class="data-item">
                            <span class="data-key">{{ $t("alarm.timeline.loop") }}:</span>
                            <span class="data-value">
                              <b-badge
                                :variant="
                                  step.node_data.data.loop ? 'success' : 'secondary'
                                "
                              >
                                {{
                                  step.node_data.data.loop
                                    ? $t("alarm.timeline.yes")
                                    : $t("alarm.timeline.no")
                                }}
                              </b-badge>
                            </span>
                          </div>
                          <div v-if="step.node_data.data.radius" class="data-item">
                            <span class="data-key"
                              >{{ $t("alarm.timeline.radius") }}:</span
                            >
                            <span class="data-value">{{
                              step.node_data.data.radius
                            }}</span>
                          </div>
                          <div v-if="step.node_data.data.layout" class="data-item">
                            <span class="data-key"
                              >{{ $t("alarm.timeline.layout") }}:</span
                            >
                            <span class="data-value">{{
                              step.node_data.data.layout
                            }}</span>
                          </div>
                          <div v-if="step.node_data.data.delay" class="data-item">
                            <span class="data-key"
                              >{{ $t("alarm.timeline.delay_time") }}:</span
                            >
                            <span class="data-value"
                              >{{ step.node_data.data.delay }}s</span
                            >
                          </div>
                        </div>
                      </div>

                      <!-- Generic data display for other commands -->
                      <div v-else class="data-grid mt-1">
                        <div
                          v-for="(value, key) in step.node_data.data"
                          :key="key"
                          class="data-item"
                        >
                          <span class="data-key">{{ key }}:</span>
                          <span class="data-value">{{ formatDataValue(value) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Filled Stack -->
                    <div
                      v-if="
                        step.node_data.filled_stack &&
                        step.node_data.filled_stack.length > 0
                      "
                      class="filled-stack mt-2"
                    >
                      <div class="d-flex align-items-center py-1">
                        <b-button
                          variant="outline-info"
                          size="sm"
                          class="ml-1"
                          @click="toggleStackView(historyIndex, stepIndex)"
                        >
                          {{
                            showStack[`${historyIndex}-${stepIndex}`]
                              ? "Ocultar"
                              : "Ver Stack"
                          }}
                        </b-button>
                      </div>
                      <b-collapse v-model="showStack[`${historyIndex}-${stepIndex}`]">
                        <pre class="json-preview">{{
                          JSON.stringify(step.node_data.filled_stack, null, 2)
                        }}</pre>
                      </b-collapse>
                    </div>
                  </div>
                </div>

                <!-- Terminate/PartialTerminate Step -->
                <div
                  v-else-if="
                    step.type === 'Terminate' || step.type === 'PartialTerminate'
                  "
                  class="step-terminate"
                >
                  <div class="step-header d-flex align-items-center">
                    <strong>{{ getTerminalTypeLabel(step.type) }}</strong>
                    <small v-if="step.data.timestamp" class="text-muted ml-2">
                      {{ formatDate(step.data.timestamp) }}
                    </small>
                  </div>
                  <div v-if="step.meta && step.meta.reason" class="terminate-reason mt-2">
                    <strong>{{ $t("alarm.timeline.reason") }}:</strong>
                    {{ step.meta.reason }}
                  </div>
                  <!-- Meta información adicional -->
                  <div v-if="step.meta" class="terminate-meta mt-2">
                    <div v-if="step.meta.created_at" class="info-item">
                      <span class="info-label"
                        >{{ $t("alarm.timeline.created_at") }}:</span
                      >
                      <span class="info-value">{{
                        formatDate(step.meta.created_at)
                      }}</span>
                    </div>
                    <div v-if="step.meta.source_timestamp" class="info-item">
                      <span class="info-label"
                        >{{ $t("alarm.timeline.source_timestamp") }}:</span
                      >
                      <span class="info-value">{{
                        formatDate(step.meta.source_timestamp)
                      }}</span>
                    </div>
                  </div>
                  <!-- Comentarios si existen -->
                  <div v-if="step.data.comments" class="step-comments mt-2">
                    <strong>{{ $t("alarm.timeline.comments") }}:</strong>
                    <div class="comments-text">{{ step.data.comments }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>

    <!-- No data message -->
    <div v-if="!histories || histories.length === 0" class="text-center text-muted p-4">
      <p>{{ $t("alarm.timeline.no_items") }}</p>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import { BCollapse, BBadge, BButton } from "bootstrap-vue";

export default {
  name: "ActionPlanLogs",
  components: {
    BCollapse,
    BBadge,
    BButton,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const openHistories = ref({});
    const showStack = ref({});

    const hasData = computed(() => {
      return props.item?.data?.history && props.item.data.history.length > 0;
    });

    const histories = computed(() => {
      return props.item?.data?.history || [];
    });

    const toggleHistoryCollapse = (index) => {
      openHistories.value = {
        ...openHistories.value,
        [index]: !openHistories.value[index],
      };
    };

    const toggleStackView = (historyIndex, stepIndex) => {
      const key = `${historyIndex}-${stepIndex}`;
      showStack.value = {
        ...showStack.value,
        [key]: !showStack.value[key],
      };
    };

    const getActionPlanName = (history) => {
      const actionPlanStep = history.path
        ?.slice(1)
        .find((step) => step.type === "ActionPlan");
      return actionPlanStep?.name || "Action Plan";
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    };

    const formatDuration = (startedAt, endedAt) => {
      if (!startedAt || !endedAt) return "N/A";
      const start = new Date(startedAt);
      const end = new Date(endedAt);
      const diffSeconds = Math.round((end - start) / 1000);
      return `${diffSeconds}s`;
    };

    const getTerminalBadgeVariant = (terminalType) => {
      switch (terminalType) {
        case "Terminate":
          return "success";
        case "PartialTerminate":
          return "warning";
        default:
          return "danger";
      }
    };

    const getTerminalTypeLabel = (terminalType) => {
      switch (terminalType) {
        case "Terminate":
          return "Terminado";
        case "PartialTerminate":
          return "Parcialmente Terminado";
        default:
          return terminalType;
      }
    };

    const getTerminalIcon = (terminalType) => {
      switch (terminalType) {
        case "Terminate":
          return "CheckCircleIcon";
        case "PartialTerminate":
          return "AlertCircleIcon";
        default:
          return "XCircleIcon";
      }
    };

    const getStepIcon = (stepType, step = null) => {
      switch (stepType) {
        case "ActionPlan":
          return "ClipboardIcon";
        case "Decision":
          return "GitBranchIcon";
        case "Node":
          // Si es un Node con filled_stack, usar icono de comando
          if (
            step &&
            step.node_data &&
            step.node_data.filled_stack &&
            step.node_data.filled_stack.length > 0
          ) {
            return "TerminalIcon";
          }
          return "PlayIcon";
        case "Terminate":
          return "CheckCircleIcon";
        case "PartialTerminate":
          return "AlertCircleIcon";
        default:
          return "CircleIcon";
      }
    };

    const getStepIconColor = (stepType) => {
      switch (stepType) {
        case "ActionPlan":
          return "blue";
        case "Decision":
          return "yellow";
        case "Node":
          return "green";
        case "Terminate":
          return "blue";
        case "PartialTerminate":
          return "yellow";
        default:
          return "grey";
      }
    };

    const getStatusBadgeVariant = (status) => {
      switch (status?.toLowerCase()) {
        case "ok":
          return "success";
        case "error":
          return "danger";
        case "pending":
          return "warning";
        default:
          return "secondary";
      }
    };

    const getSubtypeBadgeVariant = (subtype) => {
      switch (subtype?.toLowerCase()) {
        case "command":
          return "info";
        default:
          return "secondary";
      }
    };

    const formatDataValue = (value) => {
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      if (typeof value === "object" && value !== null) {
        return JSON.stringify(value);
      }
      return String(value);
    };

    return {
      hasData,
      histories,
      openHistories,
      showStack,
      toggleHistoryCollapse,
      toggleStackView,
      getActionPlanName,
      formatDate,
      formatDuration,
      getTerminalBadgeVariant,
      getTerminalTypeLabel,
      getTerminalIcon,
      getStepIcon,
      getStepIconColor,
      getStatusBadgeVariant,
      getSubtypeBadgeVariant,
      formatDataValue,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables/_variables.scss";

.action-plan-logs-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.action-plan-history-card {
  border-radius: 0.5rem;
  overflow: hidden;

  .dark-layout & {
    border-color: $theme-dark-border-color;
    background-color: $theme-dark-card-bg;
  }
}

.action-plan-header {
  background-color: #f8f9fa;
  padding: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #dee2e6;

  &:hover {
    background-color: #e9ecef;
  }

  .dark-layout & {
    background-color: $theme-dark-table-header-bg;
    border-bottom-color: $theme-dark-border-color;

    &:hover {
      background-color: lighten($theme-dark-table-header-bg, 5%);
    }
  }
}

.action-plan-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: #495057;

  .dark-layout & {
    color: $theme-dark-headings-color;
  }
}

.chevron-icon {
  transition: transform 0.3s ease;
  color: #6c757d;

  &.rotated {
    transform: rotate(180deg);
  }

  .dark-layout & {
    color: $theme-dark-text-muted-color;
  }
}

.action-plan-collapse {
  &.collapse {
    border-radius: 0 0 0 0;
  }
  .collapse-content {
    .dark-layout & {
      background-color: $theme-dark-card-bg;
    }
  }
}

.action-plan-body {
  padding: 0.75rem;
}

.plan-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;

  .dark-layout & {
    background-color: $theme-dark-table-bg;
    border-color: $theme-dark-border-color;
  }
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.25rem;
}

.info-label {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.9rem;

  .dark-layout & {
    color: $theme-dark-text-muted-color;
  }
}

.info-value {
  text-align: right;
  flex-grow: 1;
  padding-left: 1rem;
  font-size: 0.9rem;

  .dark-layout & {
    color: $theme-dark-label-color;
  }
}

.steps-timeline {
  margin-top: 1.5rem;

  h6 {
    color: #495057;
    font-weight: 600;
    margin-bottom: 1rem;

    .dark-layout & {
      color: $theme-dark-headings-color;
    }
  }
}

.timeline-step {
  position: relative;
  display: flex;
  flex-direction: row;
  padding-block: 0.75rem;
  gap: 0.75rem;
  align-items: flex-start;

  &::before {
    content: "";
    position: absolute;
    top: 1.5rem;
    left: 1.25rem;
    transform: translateX(-50%);
    width: 2px;
    height: calc(100% - 1rem);
    background-color: #e9ecef;
    z-index: 1;

    .dark-layout & {
      background-color: $theme-dark-border-color;
    }
  }

  &:last-child::before {
    display: none;
  }
}

.step-icon-container {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 0.4rem;
  height: fit-content;
  min-width: 2.5rem;
  min-height: 2.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &.blue {
    background-color: #007bff;
  }

  &.green {
    background-color: #28a745;
  }

  &.yellow {
    background-color: #ffc107;
  }

  &.grey {
    background-color: #6c757d;
  }

  .step-icon {
    color: white;
  }
}

.step-content {
  flex: 1;
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .dark-layout & {
    background-color: $theme-dark-card-bg;
    border-color: $theme-dark-border-color;
  }
}

.step-header {
  border-bottom: 1px solid #f1f1f1;
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;

  strong {
    color: #495057;
    font-size: 1rem;

    .dark-layout & {
      color: $theme-dark-headings-color;
    }
  }

  .dark-layout & {
    border-bottom-color: $theme-dark-border-color;
  }
}

.decision-content,
.node-content {
  .question,
  .answer {
    margin-bottom: 0.5rem;

    strong {
      color: #6c757d;
      font-size: 0.9rem;

      .dark-layout & {
        color: $theme-dark-text-muted-color;
      }
    }
  }
}

.options-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.camera-round-data {
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;

  .dark-layout & {
    background-color: $theme-dark-table-bg;
    border-color: $theme-dark-border-color;
  }
}

.node-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem 1.5rem;
  margin-bottom: 1rem;
}

.node-data {
  background-color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;

  .dark-layout & {
    background-color: $theme-dark-table-bg;
    border-color: $theme-dark-border-color;
  }

  strong {
    color: #495057;
    font-size: 0.9rem;
    display: block;
    margin-bottom: 0.5rem;

    .dark-layout & {
      color: $theme-dark-headings-color;
    }
  }
}

.data-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.25rem;
}

.data-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  padding: 0.25rem 0;

  .data-key {
    font-weight: 600;
    color: #6c757d;
    margin-right: 0.5rem;

    .dark-layout & {
      color: $theme-dark-text-muted-color;
    }
  }

  .data-value {
    text-align: right;
    flex: 1;
    word-break: break-word;

    .dark-layout & {
      color: $theme-dark-label-color;
    }
  }
}

.filled-stack {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;

  .dark-layout & {
    background-color: $theme-dark-table-bg;
    border-color: $theme-dark-border-color;
  }

  strong {
    color: #495057;
    font-size: 0.9rem;

    .dark-layout & {
      color: $theme-dark-headings-color;
    }
  }
}

.json-preview {
  background-color: #212529;
  color: #f8f9fa;
  padding: 0.75rem;
  border-radius: 0.375rem;
  font-family: "SF Mono", "Fira Code", "Courier New", monospace;
  font-size: 0.8rem;
  line-height: 1.4;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--second-dark);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: var(--second-dark);
  }

  /* Para Firefox */
  scrollbar-width: thin;
  scrollbar-color: var(--second-dark) transparent;
}

.step-terminate {
  .terminate-reason {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    padding: 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.9rem;

    .dark-layout & {
      background-color: rgba(255, 193, 7, 0.1);
      border-color: rgba(255, 193, 7, 0.3);
      color: $theme-dark-label-color;
    }

    strong {
      color: #856404;

      .dark-layout & {
        color: #ffc107;
      }
    }
  }

  .terminate-meta {
    .info-item {
      display: flex;
      justify-content: space-between;
      padding: 0.25rem 0;
      border-bottom: 1px solid #e9ecef;
      .dark-layout & {
        border-bottom-color: $theme-dark-border-color;
      }

      &:last-child {
        border-bottom: none;
      }
    }
  }
}

.step-files,
.step-comments {
  .files-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .comments-text {
    background-color: #f8f9fa;
    padding: 0.5rem;
    border-radius: 0.25rem;
    border-left: 3px solid #007bff;
    font-style: italic;
    margin-top: 0.25rem;

    .dark-layout & {
      background-color: $theme-dark-table-bg;
      color: $theme-dark-label-color;
      border-left-color: #0d6efd;
    }
  }
}

.user-info {
  .user-name {
    font-weight: 600;
    color: #495057;

    .dark-layout & {
      color: $theme-dark-headings-color;
    }
  }

  .user-email {
    font-size: 0.85rem;
    opacity: 0.8;
  }
}

.step-details {
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;

  .dark-layout & {
    background-color: $theme-dark-table-bg;
  }

  .info-item {
    display: flex;
    justify-content: space-between;
    padding: 0.25rem 0;
    font-size: 0.85rem;

    &:not(:last-child) {
      border-bottom: 1px solid #e9ecef;

      .dark-layout & {
        border-bottom-color: $theme-dark-border-color;
      }
    }
  }
}

// Responsive
@media (max-width: 768px) {
  .plan-info-grid {
    grid-template-columns: 1fr;
  }

  .node-grid {
    grid-template-columns: 1fr;
  }

  .info-item,
  .data-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;

    .info-value,
    .data-value {
      text-align: left;
      padding-left: 0;
    }
  }

  .action-plan-header {
    .d-flex {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;

      &.justify-content-between {
        align-items: stretch;
      }
    }
  }
}
</style>
